import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import moment from "moment";

// ---------- helpers ----------

const formatDate = (date: any) => {
  if (!date) return "--";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "--";
};

const formatDateTime = (date: any) => {
  if (!date) return "--";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "--";
};

const displayValue = (value: any) =>
  value === null || value === undefined || value === "" ? "--" : value;

const formatCurrency = (value: any) => {
  if (value === null || value === undefined || value === "") return "--";
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  return `₹ ${num.toLocaleString("en-IN")}`;
};

const hasValue = (v: any) => v !== null && v !== undefined && v !== "";

const statusMeta = (status: any) => {
  switch (status) {
    case "Y":
      return { label: "Confirmed", color: "success" as const };
    case "N":
      return { label: "Not Confirmed", color: "warning" as const };
    default:
      return { label: displayValue(status), color: "default" as const };
  }
};

// ---------- small presentational bits ----------

const DetailItem = ({ label, value }: { label: string; value: any }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
      {value}
    </Typography>
  </Grid>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, mt: 3 }}>
    {children}
  </Typography>
);

// ---------- main component ----------

const PanDemandResponseModal = ({
  state,
  isModalOpen,
  closeModal,
  style,
  handleItemClick,
  getFileName,
}: any) => {
  const response = state?.latestDemandResponse?.[0];

  const status = statusMeta(response?.confirmStatus);

  const orderSectionHasData =
    hasValue(response?.orderPassedBy) ||
    hasValue(response?.orderDt) ||
    hasValue(response?.orderRefNum) ||
    hasValue(response?.demandResponseId) ||
    hasValue(response?.applicationDt);

  const hasChallanDetails = response?.challanDetailsList?.length > 0;
  const hasAttachments = response?.attachments?.length > 0;
  const hasRemarks = hasValue(response?.remarks);

  return (
    <Modal open={isModalOpen} onClose={closeModal}>
      <Box
        sx={{
          ...style,
          zIndex: 100,
          height: "85vh",
          width: "75vw",
          maxWidth: 960,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 2,
          bgcolor: "background.paper",
          boxShadow: 24,
          outline: "none",
        }}
      >
        {!response ? (
          <Box sx={{ p: 4 }}>
            <Typography>No demand response details available.</Typography>
          </Box>
        ) : (
          <>
            {/* ---------- Sticky header ---------- */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                bgcolor: "grey.50",
              }}
            >
              <Box sx={{ pr: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Demand Response Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {displayValue(response.arrearDemandRsnCd)}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                  <Chip size="small" label={status.label} color={status.color} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Txn: ${displayValue(response.transactionNo)}`}
                  />
                </Stack>
              </Box>

              <Stack alignItems="flex-end" spacing={0.5}>
                <IconButton size="small" onClick={closeModal}   sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  color: "#666",
                  backgroundColor: "#F5F5F5",
                  "&:hover": {
                    backgroundColor: "#E5E5E5",
                  },
                }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  Current Demand Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "error.main" }}>
                  {formatCurrency(response.currentDemandAmt)}
                </Typography>
              </Stack>
            </Box>

            {/* ---------- Scrollable body ---------- */}
            <Box sx={{ flex: 1, overflow: "auto", px: 3, pb: 3 }}>
              {/* Core details */}
              <SectionTitle>Response Summary</SectionTitle>
              <Grid container spacing={2}>
                <DetailItem label="Demand Date" value={formatDate(response.demandDt)} />
                <DetailItem label="Date of Response" value={formatDateTime(response.confirmDt)} />
                <DetailItem label="Updated By" value={displayValue(response.updatedBy)} />
                <DetailItem label="Last Updated" value={formatDateTime(response.lastUpdatedAt)} />
                <DetailItem
                  label="Arrear Demand Reason ID"
                  value={displayValue(response.arrearDemandRsnId)}
                />
                <DetailItem label="Un-Paid Amount" value={response?.unpaidAmt ? formatCurrency(response.unpaidAmt) : "--"} />
                <DetailItem label="Response Type" value={response.responseType ? response.responseType : "--"} />
              </Grid>

              {/* Order / reference details — only if present */}
              {orderSectionHasData && (
                <>
                  <Divider sx={{ mt: 3 }} />
                  <SectionTitle>Order &amp; Reference Details</SectionTitle>
                  <Grid container spacing={2}>
                    {hasValue(response.orderPassedBy) && (
                      <DetailItem label="Order Passed By" value={response.orderPassedBy} />
                    )}
                    {hasValue(response.orderDt) && (
                      <DetailItem label="Order Date" value={formatDate(response.orderDt)} />
                    )}
                    {hasValue(response.orderRefNum) && (
                      <DetailItem label="Order Reference Number" value={response.orderRefNum} />
                    )}
                    {hasValue(response.demandResponseId) && (
                      <DetailItem label="Demand Response ID" value={response.demandResponseId} />
                    )}
                    {hasValue(response.applicationDt) && (
                      <DetailItem
                        label="Application Date"
                        value={formatDate(Number(response.applicationDt))}
                      />
                    )}
                  </Grid>
                </>
              )}

              {/* Challan details */}
              {hasChallanDetails && (
                <>
                  <Divider sx={{ mt: 3 }} />
                  <SectionTitle>Challan Details</SectionTitle>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.50" }}>
                          {["CIN", "DIN", "BSR Code", "AY", "Amount", "Payment Date", "Minor Head", "Serial No"].map(
                            (h) => (
                              <TableCell key={h} sx={{ fontWeight: 700 }}>
                                {h}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {response.challanDetailsList.map((challan: any, index: number) => (
                          <TableRow key={index} hover>
                            <TableCell>{displayValue(challan.cin)}</TableCell>
                            <TableCell>{displayValue(challan.din)}</TableCell>
                            <TableCell>{displayValue(challan.bsrCd)}</TableCell>
                            <TableCell>{displayValue(challan.itrAy)}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {formatCurrency(challan.cinAmt)}
                            </TableCell>
                            <TableCell>
                              {challan.pymntDt ? formatDate(Number(challan.pymntDt)) : "--"}
                            </TableCell>
                            <TableCell>{displayValue(challan.minorHead)}</TableCell>
                            <TableCell>{displayValue(challan.serialNum)}</TableCell>
                            {/* <TableCell>
                              {challan.challanAttachment ? (
                                <Chip
                                  size="small"
                                  icon={<DescriptionIcon fontSize="small" />}
                                  label={challan.challanAttachment.attachmentName}
                                  clickable
                                  onClick={() =>
                                    handleItemClick(
                                      challan.challanAttachment.docPath,
                                      response.storageSystem
                                    )
                                  }
                                  sx={{ maxWidth: 180 }}
                                />
                              ) : (
                                "--"
                              )}
                            </TableCell> */}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Attachments */}
              {hasAttachments && (
                <>
                  <Divider sx={{ mt: 3 }} />
                  <SectionTitle>Attachments</SectionTitle>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {response.attachments.map((detail: any, index: number) => (
                      <Chip
                        key={index}
                        icon={<AttachFileIcon fontSize="small" />}
                        label={getFileName(detail, response.storageSystem)}
                        clickable
                        variant="outlined"
                        onClick={() => handleItemClick(detail, response.storageSystem)}
                      />
                    ))}
                  </Stack>
                </>
              )}

              {/* Remarks */}
              {hasRemarks && (
                <>
                  <Divider sx={{ mt: 2 }} />
                  <SectionTitle>Remarks</SectionTitle>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderLeft: "4px solid",
                      borderLeftColor: "primary.main",
                      bgcolor: "grey.50",
                      maxHeight: 220,
                      overflow: "auto",
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                      {response.remarks}
                    </Typography>
                  </Paper>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default PanDemandResponseModal;